import { EFieldToneRule } from "../../../../../constants/enum/field-tone-rule";
import { EFieldValueFormat } from "../../../../../constants/enum/field-value-format";
import { Ti18nKeys } from "../../../../../constants/i18n";
import { TUserKeys } from "../../../../../Types";

export type TUserFieldDefinition = {
  userKey: TUserKeys;
  labelKey: Ti18nKeys;
  isMachineValue: boolean;
  toneRule: EFieldToneRule;
  valueFormat: EFieldValueFormat;
};

export const VERIFIED_STATUS_VALUE = "verified";
export const AFFIRMATIVE_VALUE = "YES";
export const ZERO_VALUE = "0";

export const FIELD_GROUP_COLUMNS = 4;

export const IDENTITY_FIELDS: TUserFieldDefinition[] = [
  {
    userKey: "username",
    labelKey: "USERNAME",
    isMachineValue: false,
    toneRule: EFieldToneRule.NEUTRAL,
    valueFormat: EFieldValueFormat.RAW
  },
  {
    userKey: "userId",
    labelKey: "USER_ID",
    isMachineValue: true,
    toneRule: EFieldToneRule.NEUTRAL,
    valueFormat: EFieldValueFormat.RAW
  },
  {
    userKey: "profilePublicID",
    labelKey: "PROFILE_ID",
    isMachineValue: true,
    toneRule: EFieldToneRule.NEUTRAL,
    valueFormat: EFieldValueFormat.RAW
  },
  {
    userKey: "referralCode",
    labelKey: "REFERRAL_CODE",
    isMachineValue: true,
    toneRule: EFieldToneRule.NEUTRAL,
    valueFormat: EFieldValueFormat.RAW
  },
  {
    userKey: "gender",
    labelKey: "GENDER",
    isMachineValue: false,
    toneRule: EFieldToneRule.NEUTRAL,
    valueFormat: EFieldValueFormat.RAW
  },
  {
    userKey: "religion",
    labelKey: "RELIGION",
    isMachineValue: false,
    toneRule: EFieldToneRule.NEUTRAL,
    valueFormat: EFieldValueFormat.RAW
  },
  {
    userKey: "subreligion",
    labelKey: "SUBRELIGION",
    isMachineValue: false,
    toneRule: EFieldToneRule.NEUTRAL,
    valueFormat: EFieldValueFormat.RAW
  },
  {
    userKey: "userAppLanguage",
    labelKey: "APP_LANGUAGE",
    isMachineValue: false,
    toneRule: EFieldToneRule.NEUTRAL,
    valueFormat: EFieldValueFormat.RAW
  },
  {
    userKey: "originCountry",
    labelKey: "ORIGIN_COUNTRY",
    isMachineValue: false,
    toneRule: EFieldToneRule.NEUTRAL,
    valueFormat: EFieldValueFormat.RAW
  },
  {
    userKey: "residenceCountry",
    labelKey: "RESIDENCE_COUNTRY",
    isMachineValue: false,
    toneRule: EFieldToneRule.NEUTRAL,
    valueFormat: EFieldValueFormat.RAW
  },
  {
    userKey: "cityName",
    labelKey: "CITY_NAME",
    isMachineValue: false,
    toneRule: EFieldToneRule.NEUTRAL,
    valueFormat: EFieldValueFormat.RAW
  },
  {
    userKey: "statusChangeDate",
    labelKey: "STATUS_CHANGE_DATE",
    isMachineValue: false,
    toneRule: EFieldToneRule.NEUTRAL,
    valueFormat: EFieldValueFormat.RAW
  },
  {
    userKey: "khattabaAssigned",
    labelKey: "KHATTABA_ASSIGNED",
    isMachineValue: false,
    toneRule: EFieldToneRule.NEUTRAL,
    valueFormat: EFieldValueFormat.RAW
  }
];

export const ACTIVATION_AND_VERIFICATION_FIELDS: TUserFieldDefinition[] = [
  {
    userKey: "verificationStatus",
    labelKey: "ACTIVATION_STATUS",
    isMachineValue: false,
    toneRule: EFieldToneRule.POSITIVE_WHEN_ACTIVATED,
    valueFormat: EFieldValueFormat.ACTIVATION_STATUS
  },
  {
    userKey: "numberOfVerifiedApprovedPictures",
    labelKey: "APPROVED_PICTURES",
    isMachineValue: false,
    toneRule: EFieldToneRule.CRITICAL_WHEN_ZERO,
    valueFormat: EFieldValueFormat.RAW
  },
  {
    userKey: "selfieVerificationCompletedOn",
    labelKey: "SELFIE_COMPLETED_ON",
    isMachineValue: false,
    toneRule: EFieldToneRule.NEUTRAL,
    valueFormat: EFieldValueFormat.RAW
  },
  {
    userKey: "verificationCode",
    labelKey: "ACTIVATION_CODE",
    isMachineValue: true,
    toneRule: EFieldToneRule.NEUTRAL,
    valueFormat: EFieldValueFormat.RAW
  },
  {
    userKey: "facebookVerify",
    labelKey: "FACEBOOK_VERIFY",
    isMachineValue: false,
    toneRule: EFieldToneRule.POSITIVE_WHEN_YES,
    valueFormat: EFieldValueFormat.ACTIVATION_LINK
  },
  {
    userKey: "smsVerify",
    labelKey: "SMS_VERIFY",
    isMachineValue: false,
    toneRule: EFieldToneRule.POSITIVE_WHEN_YES,
    valueFormat: EFieldValueFormat.ACTIVATION_LINK
  },
  {
    userKey: "emailVerify",
    labelKey: "EMAIL_VERIFY",
    isMachineValue: false,
    toneRule: EFieldToneRule.POSITIVE_WHEN_YES,
    valueFormat: EFieldValueFormat.ACTIVATION_LINK
  },
  {
    userKey: "appleVerify",
    labelKey: "APPLE_VERIFY",
    isMachineValue: false,
    toneRule: EFieldToneRule.POSITIVE_WHEN_YES,
    valueFormat: EFieldValueFormat.ACTIVATION_LINK
  },
  {
    userKey: "emailNotification",
    labelKey: "EMAIL_NOTIFICATION",
    isMachineValue: false,
    toneRule: EFieldToneRule.POSITIVE_WHEN_YES,
    valueFormat: EFieldValueFormat.TOGGLE_STATE
  },
  {
    userKey: "pushNotification",
    labelKey: "PUSH_NOTIFICATION",
    isMachineValue: false,
    toneRule: EFieldToneRule.POSITIVE_WHEN_YES,
    valueFormat: EFieldValueFormat.TOGGLE_STATE
  }
];

export const DEVICE_FIELDS: TUserFieldDefinition[] = [
  {
    userKey: "phoneDetails",
    labelKey: "PHONE_DETAILS",
    isMachineValue: false,
    toneRule: EFieldToneRule.NEUTRAL,
    valueFormat: EFieldValueFormat.RAW
  },
  {
    userKey: "mobileAppPlatform",
    labelKey: "PLATFORM",
    isMachineValue: false,
    toneRule: EFieldToneRule.NEUTRAL,
    valueFormat: EFieldValueFormat.RAW
  },
  {
    userKey: "mobileAppVersion",
    labelKey: "APP_VERSION",
    isMachineValue: false,
    toneRule: EFieldToneRule.NEUTRAL,
    valueFormat: EFieldValueFormat.RAW
  },
  {
    userKey: "loginCount",
    labelKey: "LOGIN_COUNT",
    isMachineValue: false,
    toneRule: EFieldToneRule.NEUTRAL,
    valueFormat: EFieldValueFormat.RAW
  },
  {
    userKey: "lastLogin",
    labelKey: "LAST_LOGIN",
    isMachineValue: false,
    toneRule: EFieldToneRule.NEUTRAL,
    valueFormat: EFieldValueFormat.RAW
  },
  {
    userKey: "ipAddress",
    labelKey: "IP_ADDRESS",
    isMachineValue: true,
    toneRule: EFieldToneRule.NEUTRAL,
    valueFormat: EFieldValueFormat.RAW
  },
  {
    userKey: "deviceId",
    labelKey: "DEVICE_ID",
    isMachineValue: true,
    toneRule: EFieldToneRule.NEUTRAL,
    valueFormat: EFieldValueFormat.RAW
  },
  {
    userKey: "device_token",
    labelKey: "DEVICE_TOKEN",
    isMachineValue: true,
    toneRule: EFieldToneRule.NEUTRAL,
    valueFormat: EFieldValueFormat.RAW
  }
];
